import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
  HStack,
  VStack,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Icon,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import { FiRefreshCw, FiUsers, FiLayers, FiClock } from 'react-icons/fi';

const API_BASE = '/api';

function TenantOverview() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchTenants = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/tenants/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setTenants(data.tenants || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const totalRecords = tenants.reduce(
    (sum, t) => sum + t.customers + t.subscriptions + t.payments + t.refunds, 0
  );
  const activeTenants = tenants.filter(t => t.tenant_id !== '').length;

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Tenant Overview</Heading>
          <Text color="gray.500">Multi-tenancy usage across all tenants</Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="teal" onClick={fetchTenants}>
          Refresh
        </Button>
      </HStack>

      {/* Summary cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="teal.100" borderRadius="lg">
              <Icon as={FiLayers} boxSize={6} color="teal.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Total Tenants</StatLabel>
              <StatNumber fontSize="2xl">{tenants.length}</StatNumber>
            </VStack>
          </HStack>
        </Stat>

        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="green.100" borderRadius="lg">
              <Icon as={FiUsers} boxSize={6} color="green.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Active Tenants</StatLabel>
              <StatNumber fontSize="2xl">{activeTenants}</StatNumber>
            </VStack>
          </HStack>
        </Stat>

        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="blue.100" borderRadius="lg">
              <Icon as={FiClock} boxSize={6} color="blue.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Total Records</StatLabel>
              <StatNumber fontSize="2xl">{totalRecords.toLocaleString()}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
      </SimpleGrid>

      {/* Tenant table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Tenant</Th>
              <Th isNumeric>Customers</Th>
              <Th isNumeric>Subscriptions</Th>
              <Th isNumeric>Payments</Th>
              <Th isNumeric>Refunds</Th>
              <Th isNumeric>Webhooks</Th>
              <Th isNumeric>Settings</Th>
              <Th>First Seen</Th>
              <Th>Last Active</Th>
              <Th>Share</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tenants.map((t, idx) => {
              const tenantTotal = t.customers + t.subscriptions + t.payments + t.refunds;
              const share = totalRecords > 0 ? (tenantTotal / totalRecords * 100) : 0;
              return (
                <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <Badge
                      colorScheme={t.tenant_id === '' ? 'gray' : 'teal'}
                      fontSize="sm"
                      px={2}
                      py={1}
                    >
                      {t.label}
                    </Badge>
                  </Td>
                  <Td isNumeric fontWeight="medium">{t.customers}</Td>
                  <Td isNumeric>
                    <Tooltip label={`${t.active_subscriptions} active`}>
                      <Text as="span">{t.subscriptions}</Text>
                    </Tooltip>
                  </Td>
                  <Td isNumeric fontWeight="medium">{t.payments}</Td>
                  <Td isNumeric>{t.refunds}</Td>
                  <Td isNumeric>{t.webhooks}</Td>
                  <Td isNumeric>{t.settings}</Td>
                  <Td fontSize="sm" color="gray.600">
                    {t.first_seen ? new Date(t.first_seen).toLocaleDateString() : '—'}
                  </Td>
                  <Td fontSize="sm" color="gray.600">
                    {t.last_active ? new Date(t.last_active).toLocaleDateString() : '—'}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Progress
                        value={share}
                        size="sm"
                        colorScheme="teal"
                        borderRadius="full"
                        w="60px"
                      />
                      <Text fontSize="xs" color="gray.500">{share.toFixed(1)}%</Text>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        {tenants.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No tenant data found</Text>
        )}
      </Box>
    </Box>
  );
}

export default TenantOverview;
